import { useCallback, useEffect, useMemo, useState } from 'react';
import { TablePaginationConfig } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import { SortDirection } from '../model/enum';

const useTableConfig = (pageResetArgs: any[] = []) => {
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
  });
  const [sorter, setSorter] = useState<
    | {
        field: string;
        order: SortOrder;
      }
    | undefined
  >();

  const getColumnSorter = useCallback(
    (columnName: string) => {
      return sorter?.field === columnName ? sorter?.order : null;
    },
    [sorter],
  );

  useEffect(() => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: 1,
    }));
  }, [pagination.pageSize, ...pageResetArgs]);

  const pageSize = useMemo(() => {
    if (!pagination || pagination.pageSize === null || pagination.pageSize === undefined) {
      return 10;
    }
    return pagination.pageSize;
  }, [pagination]);

  const currentPage = useMemo(() => {
    if (!pagination || pagination.current === null || pagination.current === undefined) {
      return 0;
    }
    return pagination.current - 1;
  }, [pagination]);

  const sortBy = useMemo(() => {
    if (!sorter) {
      return 'id';
    }
    return sorter.field;
  }, [sorter]);

  const direction: SortDirection = useMemo(() => {
    if (!sorter) {
      return 'desc';
    }
    return sorter.order === 'ascend' ? 'asc' : 'desc';
  }, [sorter]);

  const setTotalElements = (total: number) => {
    setPagination({
      ...pagination,
      total,
    });
  };

  const handleTableChange = (pagination: any, filter: any, sorter: any) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize,
    });
  };

  const handleSorterChange = (sorter: any) => {
    setSorter(sorter);
  };

  return {
    pageSize,
    currentPage,
    sortBy,
    direction,
    pagination,
    setTotalElements,
    getColumnSorter,
    handleTableChange,
    handlePaginationChange,
    handleSorterChange,
  };
};

export default useTableConfig;
